import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { AgreementStatus, PartnerType, type Page } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { EMPTY, enumOptions } from '@/lib/format';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data/DataTable';
import { EnumBadge } from '@/components/data/EnumBadge';
import { FilterBar, type FilterDef } from '@/components/data/FilterBar';
import { ViewSwitcher } from '@/components/data/ViewSwitcher';
import { useSavedViews } from '@/components/data/useSavedViews';
import { PartnerCreateDialog } from '@/pages/partners/PartnerCreateDialog';
import type { PartnerListItem } from '@/pages/partners/types';

const FILTERS: FilterDef[] = [
  { key: 'type', label: 'Type', type: 'enum', options: enumOptions(PartnerType) },
  { key: 'text', label: 'Search', type: 'text', placeholder: 'Name, contact, e-mail…' },
];

const COLUMNS: ColumnDef<PartnerListItem, unknown>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => <EnumBadge def={PartnerType} value={row.original.type} />,
  },
  {
    accessorKey: 'shortCode',
    header: 'Short code',
    cell: ({ row }) => (
      <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.original.shortCode}</code>
    ),
  },
  {
    accessorKey: 'contactPerson',
    header: 'Contact',
    cell: ({ row }) => row.original.contactPerson ?? <span className="text-muted-foreground">{EMPTY}</span>,
  },
  {
    accessorKey: 'email',
    header: 'E-mail',
    cell: ({ row }) =>
      row.original.email ? (
        <a
          href={`mailto:${row.original.email}`}
          className="text-primary underline-offset-4 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.original.email}
        </a>
      ) : (
        <span className="text-muted-foreground">{EMPTY}</span>
      ),
  },
  {
    accessorKey: 'agreement',
    header: 'Agreement',
    cell: ({ row }) => <EnumBadge def={AgreementStatus} value={row.original.agreement} />,
  },
  { accessorKey: 'positionsCount', header: 'Positions', meta: { align: 'right' } },
  { accessorKey: 'candidatesCount', header: 'Candidates', meta: { align: 'right' } },
  { accessorKey: 'briefingsCount', header: 'Briefings', meta: { align: 'right' } },
];

function asList(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  return typeof v === 'string' && v ? [v] : [];
}

/** Partners (spec §12): table with type filter, text search, saved views and the "New partner" dialog. */
export function PartnersListPage() {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const sv = useSavedViews('PARTNER');
  const [createOpen, setCreateOpen] = React.useState(false);

  const typeFilter = sv.state.filters.type;
  const types = React.useMemo(() => asList(typeFilter), [typeFilter]);
  const text = typeof sv.state.filters.text === 'string' ? sv.state.filters.text : undefined;
  // The API takes one `type`; several selected types are filtered on the client.
  const query = { type: types.length === 1 ? types[0] : undefined, text, pageSize: 200 };

  const list = useQuery({
    queryKey: queryKeys.list('partners', query),
    queryFn: () => api.get<Page<PartnerListItem>>('/partners', { query }),
  });

  const items = React.useMemo(() => {
    const all = list.data?.items ?? [];
    return types.length > 1 ? all.filter((p) => types.includes(p.type)) : all;
  }, [list.data, types]);

  return (
    <>
      <PageHeader
        title="Partners"
        description="Intermediaries, suppliers and freelancers."
        actions={
          canWrite ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" /> New partner
            </Button>
          ) : null
        }
      />
      <div className="space-y-3">
        <ViewSwitcher {...sv} filterDefs={FILTERS} layouts={['TABLE']} canSave={canWrite} />
        <FilterBar filters={FILTERS} values={sv.state.filters} onChange={sv.setFilters} />
        <DataTable
          columns={COLUMNS}
          data={items}
          isLoading={list.isLoading}
          rowHref={(p) => `/partners/${p.id}`}
          sorting={sv.state.sorts}
          onSortingChange={(u) => sv.setSorts(typeof u === 'function' ? u(sv.state.sorts) : u)}
          hideSearch
          caption="Partners"
          emptyTitle={list.isError ? 'Partners could not be loaded' : 'No partners yet'}
          emptyDescription={
            list.isError
              ? list.error.message
              : canWrite
                ? 'Create the first intermediary or supplier with "New partner".'
                : undefined
          }
        />
      </div>
      <PartnerCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(p) => navigate(`/partners/${p.id}`)}
      />
    </>
  );
}
